/**
 * Review grid — scrollable before/after for images and video previews.
 */

import { useTranslation } from 'react-i18next';
import type { ReviewItem, ImageReviewItem, VideoReviewItem } from '@/hooks/useAiWorkspaceFlow.ts';
import styles from './AiWorkspace.module.css';

interface Props {
  items: ReviewItem[];
  isEnhance: boolean;
  onToggleItem: (id: string) => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  isSingle?: boolean;
  /**
   * Render the GENERATED pane only, with no "Before" beside it (20260828, owner item 3).
   *
   * For the board's DEFERRED Sketch flow, where the shot this image will land on does not exist
   * yet. The flow still carries an ANCHOR shot — the scene's last one, borrowed for scope, aspect
   * and cost — so `originalDataUrl` is populated and the shipped pair would show that anchor's
   * frame under a "Before" label, promising a replacement of a shot the user never pointed at.
   * The label chip goes with the pane: there is nothing for the remaining image to be "after" of.
   *
   * Additive and default false — every other caller (the Timeline workspace, the board's own
   * write-to-this-shot Enhance and Create arms) keeps the before/after pair byte-for-byte.
   */
  generatedOnly?: boolean;
}

export function WorkspaceReview({ items, isEnhance, onToggleItem, onAcceptAll, onRejectAll, isSingle, generatedOnly }: Props) {
  const { t } = useTranslation('aiWorkspace');
  const hasSuccess = items.some(i => i.success);

  return (
    <>
      {!isSingle && hasSuccess && (
        <div className={styles.bulkActions}>
          <button className={styles.bulkBtn} onClick={onAcceptAll}>{t('acceptAll')}</button>
          <button className={styles.bulkBtn} onClick={onRejectAll}>{t('rejectAll')}</button>
        </div>
      )}
      <div className={`${styles.reviewGrid} ${isSingle ? styles.reviewGridSingle : ''}`}>
        {items.map(item => {
          if (isEnhance) {
            const img = item as ImageReviewItem;
            return (
              <div key={item.id} className={styles.reviewPair}>
                {!generatedOnly && (
                  <div className={`${styles.reviewMedia} ${styles.reviewMediaCover}`}>
                    <span className={styles.reviewMediaLabel}>{t('reviewLabelBefore')}</span>
                    {img.originalDataUrl && <img src={img.originalDataUrl} alt={t('reviewLabelOriginal')} />}
                  </div>
                )}
                <div className={`${styles.reviewMedia} ${styles.reviewMediaCover}`}>
                  {!generatedOnly && <span className={styles.reviewMediaLabel}>{t('reviewLabelAfter')}</span>}
                  {img.success && img.enhancedDataUrl ? (
                    <img src={img.enhancedDataUrl} alt={t('reviewLabelEnhanced')} />
                  ) : (
                    <div className={styles.reviewError}>{img.error ?? t('reviewFailed')}</div>
                  )}
                </div>
                {!isSingle && (
                  <div className={styles.reviewActions}>
                    <button
                      className={`${styles.reviewToggle} ${item.accepted ? styles.reviewAccepted : styles.reviewRejected}`}
                      onClick={() => onToggleItem(item.id)}
                      disabled={!item.success}
                    >
                      {item.accepted ? '\u2713' : '\u2715'}
                    </button>
                  </div>
                )}
              </div>
            );
          }

          const vid = item as VideoReviewItem;
          return (
            <div key={item.id} className={styles.reviewPair}>
              <div className={styles.reviewMedia}>
                <span className={styles.reviewMediaLabel}>
                  {vid.unitType === 'continuous' ? t('reviewLabelFromTo') : t('reviewLabelSource')}
                </span>
                {vid.unitType === 'continuous' ? (
                  <div style={{ display: 'flex', gap: 2 }}>
                    {vid.sourceThumb && <img src={vid.sourceThumb} alt={t('reviewLabelFrom')} style={{ width: '50%', objectFit: 'cover' }} />}
                    {vid.sourceThumb2 && <img src={vid.sourceThumb2} alt={t('reviewLabelTo')} style={{ width: '50%', objectFit: 'cover' }} />}
                  </div>
                ) : (
                  vid.sourceThumb && <img src={vid.sourceThumb} alt={t('reviewLabelSource')} />
                )}
              </div>
              <div className={styles.reviewMedia}>
                <span className={styles.reviewMediaLabel}>{t('reviewLabelGenerated')}</span>
                {vid.success && vid.videoUrl ? (
                  <video src={vid.videoUrl} muted autoPlay loop playsInline controls />
                ) : (
                  <div className={styles.reviewError}>{vid.error ?? t('reviewFailed')}</div>
                )}
              </div>
              {!isSingle && (
                <div className={styles.reviewActions}>
                  <button
                    className={`${styles.reviewToggle} ${item.accepted ? styles.reviewAccepted : styles.reviewRejected}`}
                    onClick={() => onToggleItem(item.id)}
                    disabled={!item.success}
                  >
                    {item.accepted ? '\u2713' : '\u2715'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
